import React, { useContext, useEffect, useRef, useState } from "react";
import { ActionItem, ActionOnItem, ArtifactType, ContentState, ContentSubstate, getArtefactIcon } from "../../../../models/SKArtifact";
import TextArtifactContainer from "./TextArtifactContainer";
import DocumentArtifactContainer from "./DocumentAtrifactContainer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CreateArtifactContext } from "../../../../context/CreateArtifactContext";
import PhotoArtifactContainer from "./PhotoArtifactContainer";
import VideoArtifactContainer from "./VideoArtifactContainer";
import AudioArtifactContainer from "./AudioArtifactContainer";
import LinkArtifactContainer from "./LinkArtifactContainer";
import EventArtifactContainer from "./EventArtifactContainer";
import LoadingPopup from "../../../channels/create/LoadingPopup";
import PlaceArtifactContainer from "./PlaceArtifactContainer";
import LetterArtifactContainer from "./LetterArtifactContainer";
import JournalArtifactContainer from "./JournalArtifactContainer";
import GreetingsArtifactContainer from "./GreetingsArtifactContainer";
import StoryArtifactContainer from "./StoryArtifactContainer";
import PollsArtifactContainer from "./PollsArtifactContainer";
import GuideArtifactContainer from "./GuideArtifactContainer";
import ListArtifactContainer from "./ListArtifactContainer";
import { useNavigationBar } from "../../../../context/NavigationContext";
import { capitalizeFirstLetter } from "../../../../utils/stringFunctions";
import { AnyObject, ObjectSchema } from "yup";
import ArtifactStateDialog, { DialoageContent } from "./components/ArtifactStateDialog";
import { deleteArtifact, updateArtifactStatus } from "../../../../api/artifacts/atrifactService";
import { useUpdateList } from "../../../contentModeration/UpdateListContext";
import PopupSingleButton, { PopupActionStatus, PopupContent } from "../../../../components/PopupSingleButton";
import Yup from "yup";

export interface ArtifactContainerRef {
  containerSchema: () => ObjectSchema<{}, AnyObject, {}, "">;
}

export default function EditArtifact() {
  const {
    contentData,
    updateContentData,
    setArtifactId,
    artifactId,
    submitEdit,
    loading,
    doneSubmitting,
    getContentActionableCreatorItems,
    containerButtonTitle,
    saveData,
    isContentEditing,
    originalState,
    isEditable,
  } = useContext(CreateArtifactContext);
  const { setNavProps } = useNavigationBar();
  const [isValidForm, setIsValidForm] = useState(false);
  //const [isSubmitted, setIsSubmitted] = useState(false);
  const [actionDone, setActionDone] = useState<PopupContent | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const { updateList } = useUpdateList();
  // const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const childRef = useRef<ArtifactContainerRef>(null);

  useEffect(() => {
    id && setArtifactId(id);
  }, [id]);

  useEffect(() => {
    const validate = async () => {
      try {
        await childRef.current?.containerSchema().validate(contentData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err) {
        let error = err as Yup.ValidationError;
        // setErrors(prev => ({ ...prev, [name]: error.message }));
        setIsValidForm(false);
        if (error.inner) {
          const errors: { path: string; message: string }[] = error.inner.map(error1 => ({
            path: error1.path || "unknown", // Field with the error
            message: error1.message, // Error message
          }));

          console.log("Validation errors:", errors);
        }
      }
    };
    validate();
  }, [contentData, childRef.current?.containerSchema]);

  function getActionDone(action: ActionItem): PopupContent | undefined {
    switch (action) {
      case ActionItem.edit_submit:
        return {
          title: "Edit Successfully Submited",
          message: "Your content is successfully submitted for edits.",
          buttonTitle: "OK",
          status: PopupActionStatus.success,
        };
      default:
        return undefined;
    }
  }

  useEffect(() => {
    setNavProps({
      backArrow: false,
      title: "Create",
      subtitle: capitalizeFirstLetter(contentData.type ?? ""),
      icon: contentData.type ? getArtefactIcon(contentData.type) : undefined,
      buttonTitle: "Submit",
      filterOption: false,
      enableLink: isEditable ? isValidForm : false,
      onNext:
        originalState?.status === ContentState.ACTIVE &&
        (originalState?.subStatus === ContentSubstate.EDIT_IN_DRAFT || originalState?.subStatus === ContentSubstate.EDIT_REJECTED)
          ? async () => {
              await submitEdit();
              //setIsSubmitted(true);
              setActionDone(getActionDone(ActionItem.edit_submit));
              // updateList(artifactId!, ContentState.ACTIVE, ContentSubstate.EDIT_IN_REVIEW);
            }
          : undefined,
      onEdit: undefined,
      onClose: async () => {
        if (
          contentData.status === ContentState.ACTIVE &&
          (contentData.subStatus === ContentSubstate.EDIT_IN_DRAFT || originalState?.subStatus === ContentSubstate.EDIT_REJECTED)
        ) {
          await saveData();
        }
        navigate(-1);
      },
    });
  }, [contentData, getContentActionableCreatorItems, isValidForm, artifactId, isContentEditing]);

  const requestEdit = async () => {
    navigate("edit");
  };

  useEffect(() => {
    if (doneSubmitting) {
      navigate("successSubmit");
    }
  }, [doneSubmitting]);

  const element = () => {
    // if (contentData.type && artifactId)
    <LoadingPopup isLoading={loading} />;
    switch (contentData.type) {
      case ArtifactType.text:
        return <TextArtifactContainer ref={childRef} />;

      case ArtifactType.audio:
        return <AudioArtifactContainer ref={childRef} />;

      case ArtifactType.document:
        return <DocumentArtifactContainer ref={childRef} />;

      case ArtifactType.event:
        return <EventArtifactContainer ref={childRef} />;

      case ArtifactType.greeting:
        return <GreetingsArtifactContainer ref={childRef} />;

      case ArtifactType.journal:
        return <JournalArtifactContainer ref={childRef} />;

      case ArtifactType.letter:
        return <LetterArtifactContainer ref={childRef} />;

      case ArtifactType.link:
        return <LinkArtifactContainer ref={childRef} />;

      case ArtifactType.list:
        return <ListArtifactContainer ref={childRef} />;

      case ArtifactType.photo:
        return <PhotoArtifactContainer ref={childRef} />;

      case ArtifactType.place:
        return <PlaceArtifactContainer ref={childRef} />;

      case ArtifactType.guide:
        return <GuideArtifactContainer ref={childRef} />;

      case ArtifactType.poll:
        return <PollsArtifactContainer ref={childRef} />;

      case ArtifactType.story:
        return <StoryArtifactContainer ref={childRef} />;

      case ArtifactType.video:
        return <VideoArtifactContainer ref={childRef} />;

      default:
        break;
    }
    return <></>;
  };

  return loading ? (
    <LoadingPopup isLoading={loading} loadingText="Loading" />
  ) : (
    <>
      {actionDone && (
        <PopupSingleButton
          popupContent={actionDone}
          onClose={async () => {
            setActionDone(undefined);
            navigate(-1);
          }}
        />
      )}
      {element()}
    </>
  );
}
